import type { ChannelRoleId } from '../types/ids';

/** Kontrollierte Texte je empfohlener Instagram-Rolle (Abschnitt 19.4). */
export interface ChannelRoleCopy {
  summary: string;
  explanation: string;
  suitableUses: string[];
  unsuitableUses: string[];
}

export const CHANNEL_ROLE_COPY: Record<ChannelRoleId, ChannelRoleCopy> = {
  primary_channel: {
    summary: 'Instagram kann ein wichtiger Kanal für Aufmerksamkeit, Vertrauen und Verkäufe sein.',
    explanation:
      'Zielgruppe, Angebot und Verkaufsweg sind bereits so weit entwickelt, dass Instagram eine tragende Rolle übernehmen kann. Die Wirkung sollte trotzdem laufend wirtschaftlich überprüft werden.',
    suitableUses: [
      'regelmäßige Sichtbarkeit und Reichweite aufbauen',
      'Vertrauen durch wiederholte Wahrnehmung stärken',
      'Interessenten in den bestehenden Verkaufsweg führen',
    ],
    unsuitableUses: [
      'vollständiger Verzicht auf eigene Kontaktwege',
      'Instagram als einzige Quelle für Geschäftsentscheidungen',
    ],
  },
  supporting_channel: {
    summary: 'Instagram kann sinnvoll sein, sollte den Verkauf aber nicht allein tragen.',
    explanation:
      'Einzelne Voraussetzungen sind gut, andere noch nicht ausreichend entwickelt. Instagram eignet sich deshalb eher für einzelne Aufgaben als für den gesamten Verkaufsprozess.',
    suitableUses: [
      'fachliche Sichtbarkeit und Vertrauensaufbau',
      'Hinführung zur eigenen Website oder E-Mail-Liste',
      'ergänzende Aufmerksamkeit neben einem anderen Hauptkanal',
    ],
    unsuitableUses: [
      'vollständige Abhängigkeit von organischer Reichweite',
      'dauerhafter manueller Verkauf über viele Einzelnachrichten',
    ],
  },
  trust_channel: {
    summary: 'Instagram eignet sich vor allem, um Vertrauen aufzubauen.',
    explanation:
      'Dein Thema wird eher gezielt gesucht als beiläufig im Feed entdeckt. Instagram kann deshalb Person, Methode und Arbeitsweise sichtbar machen, während der eigentliche Bedarf über andere Wege entsteht.',
    suitableUses: [
      'Haltung, Methode und Arbeitsweise zeigen',
      'Fallbeispiele und Erfahrungsberichte teilen',
      'Verknüpfung mit ausführlichen, suchbasierten Inhalten',
    ],
    unsuitableUses: [
      'Erwartung, dass der Feed allein den akuten Bedarf erzeugt',
      'alleinige Nutzung ohne suchbasierten Kanal',
    ],
  },
  advertising_channel: {
    summary: 'Instagram eignet sich vor allem für gezielt ausgespielte Anzeigen.',
    explanation:
      'Organische Reichweite spielt eine untergeordnete Rolle. Bezahlte Sichtbarkeit kann gezielt eingesetzt werden, sollte aber mit einem geprüften Angebot und einem klaren Verkaufsweg kombiniert werden.',
    suitableUses: [
      'gezielte Anzeigen für ein bereits erprobtes Angebot',
      'Traffic auf eine klare, gut vorbereitete Verkaufsseite',
    ],
    unsuitableUses: [
      'Anzeigen für ein noch ungeprüftes Angebot',
      'alleinige Abhängigkeit von bezahlter Reichweite',
    ],
  },
  test_later: {
    summary: 'Instagram sollte nach Aufbau der Grundlagen erneut geprüft werden.',
    explanation:
      'Ein zentraler Baustein, etwa ein erprobtes Angebot oder ein klarer Verkaufsweg, fehlt aktuell noch. Ohne diese Grundlage bleibt zusätzliche Reichweite wirtschaftlich wenig wirksam.',
    suitableUses: [
      'kleine, risikoarme Tests von Themen und Formulierungen',
      'direkte Gespräche zur Prüfung des Angebots',
    ],
    unsuitableUses: [
      'umfangreiche Content-Produktion vor Klärung der Grundlagen',
      'Instagram als alleinige Grundlage für Geschäftsentscheidungen',
    ],
  },
  currently_not_recommended: {
    summary: 'Instagram wird aktuell nicht als wichtigster Kanal empfohlen.',
    explanation:
      'Mehrere grundlegende Voraussetzungen passen derzeit nicht gut zusammen. Das bewertet nicht deine Person oder dein Angebot, sondern zeigt, dass ein anderer Kanal aktuell besser zur Zielgruppe passen könnte.',
    suitableUses: [
      'punktuelles Testen einzelner Inhalte ohne hohe Erwartungen',
      'Prüfung alternativer Kanäle mit echter Zielgruppenpräsenz',
    ],
    unsuitableUses: [
      'Instagram als alleinige Verkaufsgrundlage',
      'hoher Zeit- oder Budgetaufwand ohne vorherige Prüfung',
    ],
  },
};
